import React, { useState, useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Text,
  Input,
  Flex,
  Tag,
  Loader,
  Select,
} from '../../../components/blocks';

type Props = {
  to?: string;
};

const Root = styled.section`
  width: 100%;

  ${({ theme }) =>
    theme.screen.sp({
      padding: `${theme.spaces.xxl} 0 0`,
    })}
`;

const FormList = styled(Flex)`
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  padding: ${({ theme }) =>
    `${theme.spaces.xxl} ${theme.spaces.xxl} ${theme.spaces.l}`};
  align-items: flex-start;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.l};
  box-shadow: ${({ theme }) => theme.boxShadow.base};
  border-radius: ${({ theme }) => theme.radii.l};
`;

const FormItem = styled(Flex)`
  gap: ${({ theme }) => theme.spaces.m};
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
`;

const FormInput = styled(Flex)`
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.s};
`;

const Label = styled.label`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.fontColors.lightgrey};
`;

const FormBottom = styled(Flex)`
  padding: ${({ theme }) => theme.spaces.xl} 0;
  width: 100%;
  flex-direction: column;
  gap: ${({ theme }) => theme.spaces.l};
`;

const Links = styled.a`
  display: inline-block;
  padding: ${({ theme }) => theme.spaces.xs} 0;
  transition-duration: ${({ theme }) => theme.transitions.normal};

  &:hover {
    box-shadow: 0 1px 0 ${({ theme }) => theme.borderColors.default};
  }
`;

const Names = styled(Flex)`
  ${({ theme }) =>
    theme.screen.sp({
      flexDirection: 'column',
    })}
`;

export const EventForm: React.VFC<Props> = (pageContext) => {
  const theme = useTheme();
  const [Submitting, setSubmitting] = useState(false);

  const HandleSubmit = useCallback(() => {
    console.log('test');
    setSubmitting(true);
  }, []);

  return (
    <Root id="form">
      <Formik
        initialValues={{
          familyName: '',
          givenName: '',
          companyName: '',
          companyPosition: '',
          tel: '',
          email: '',
          comment: '',
        }}
        onSubmit={(values) => {}}
        validationSchema={Yup.object().shape({
          familyName: Yup.string().required('姓を入力してください'),
          givenName: Yup.string().required('名を入力してください'),
          companyName: Yup.string().required('医療機関名を入力してください'),
          companyPosition: Yup.string().required('役職を選択してください'),
          tel: Yup.number()
            .required('携帯電話番号を入力してください')
            .typeError('半角数字を入力してください'),
          email: Yup.string()
            .email('メールアドレスの形式に誤りがあります。')
            .required('メールアドレスを入力してください'),
        })}
      >
        {({ handleChange, handleBlur, values, errors, touched }) => (
          <form action={pageContext.to} method="post" onSubmit={HandleSubmit}>
            <FormList>
              <Text mb={theme.spaces.l} size="s" display="block" lineHeight="l">
                資料ダウンロードご希望の方は、必要事項をご記入のうえダウンロードボタンを押してください。
              </Text>

              <Flex
                width="100%"
                justifyContent="space-between"
                columnGap={theme.spaces.l}
              >
                <Names
                  width="100%"
                  justifyContent="space-between"
                  gap={theme.spaces.l}
                >
                  <FormItem>
                    <Label htmlFor="familyName">
                      姓
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>
                    <FormInput>
                      <Input
                        type="text"
                        name="familyName"
                        placeholder="例）歯科"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.familyName}
                        error={touched.familyName && errors.familyName && true}
                        width={'100%'}
                        required
                      />
                      {touched.familyName && errors.familyName ? (
                        <Text size="s" color="pink" fontWeight="bold">
                          {errors.familyName}
                        </Text>
                      ) : null}
                    </FormInput>
                  </FormItem>

                  <FormItem>
                    <Label htmlFor="givenName">
                      名
                      <Tag color="secondary" nowrap>
                        必須
                      </Tag>
                    </Label>
                    <FormInput>
                      <Input
                        type="text"
                        name="givenName"
                        placeholder="例）太郎"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.givenName}
                        error={touched.givenName && errors.givenName && true}
                        width={'100%'}
                        required
                      />
                      {touched.givenName && errors.givenName ? (
                        <Text size="s" color="pink" fontWeight="bold">
                          {errors.givenName}
                        </Text>
                      ) : null}
                    </FormInput>
                  </FormItem>
                </Names>
              </Flex>

              <FormItem>
                <Label htmlFor="companyName">
                  医療機関名
                  <Tag color="secondary" nowrap>
                    必須
                  </Tag>
                </Label>
                <FormInput>
                  <Input
                    type="text"
                    name="companyName"
                    placeholder="例）メドレー歯科"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.companyName}
                    error={touched.companyName && errors.companyName && true}
                    width={'100%'}
                    required
                  />
                  {touched.companyName && errors.companyName ? (
                    <Text size="s" color="pink" fontWeight="bold">
                      {errors.companyName}
                    </Text>
                  ) : null}
                </FormInput>
              </FormItem>

              <FormItem>
                <Label htmlFor="companyPosition">
                  役職
                  <Tag color="secondary" nowrap>
                    必須
                  </Tag>
                </Label>
                <FormInput>
                  <Select name="companyPosition" fill required>
                    <option value="">選択してください</option>
                    <option value="理事長">理事長</option>
                    <option value="理事">理事</option>
                    <option value="院長">院長</option>
                    <option value="副院長">副院長</option>
                    <option value="事務長">事務長</option>
                    <option value="歯科医師（勤務医・開業予定あり）">
                      歯科医師（勤務医・開業予定あり）
                    </option>
                    <option value="歯科医師（勤務医・開業予定なし）">
                      歯科医師（勤務医・開業予定なし）
                    </option>
                    <option value="歯科衛生士">歯科衛生士</option>
                    <option value="歯科助手">歯科助手</option>
                    <option value="受付・事務">受付・事務</option>
                    <option value="その他の医療従事者">
                      その他の医療従事者
                    </option>
                    <option value="医療関係企業従事者">
                      医療関係企業従事者
                    </option>
                  </Select>
                  {touched.companyPosition && errors.companyPosition ? (
                    <Text size="s" color="pink" fontWeight="bold">
                      {errors.companyPosition}
                    </Text>
                  ) : null}
                </FormInput>
              </FormItem>

              <FormItem>
                <Label htmlFor="tel">
                  携帯電話番号
                  <Tag color="secondary" nowrap>
                    必須
                  </Tag>
                </Label>
                <FormInput>
                  <Input
                    type="tel"
                    name="tel"
                    placeholder="例）08012345678"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.tel}
                    error={touched.tel && errors.tel && true}
                    width={'100%'}
                    required
                  />
                  {touched.tel && errors.tel ? (
                    <Text size="s" color="pink" fontWeight="bold">
                      {errors.tel}
                    </Text>
                  ) : null}
                </FormInput>
              </FormItem>

              <FormItem>
                <Label htmlFor="email">
                  メールアドレス
                  <Tag color="secondary" nowrap>
                    必須
                  </Tag>
                </Label>
                <FormInput>
                  <Input
                    type="email"
                    name="email"
                    placeholder="例）info@dentis-cloud.com"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    error={touched.email && errors.email && true}
                    width={'100%'}
                    required
                  />
                  {touched.email && errors.email ? (
                    <Text size="s" color="pink" fontWeight="bold">
                      {errors.email}
                    </Text>
                  ) : null}
                </FormInput>
              </FormItem>

              <FormBottom>
                <Text as="p" size="xs" lineHeight="m">
                  「
                  <Links
                    href="https://clinics-cloud.com/privacy"
                    target="_blank"
                  >
                    プライバシーポリシー
                  </Links>
                  」 に同意いただいた上で、送信ボタンを押してください
                </Text>

                <Button
                  type="submit"
                  size="m"
                  fill
                  wide="l"
                  radius="rounded"
                  disabled={Submitting ? true : false}
                >
                  {Submitting ? (
                    <Flex alignItems="center">
                      <Loader mr={theme.spaces.m} />
                      送信中
                    </Flex>
                  ) : (
                    'ダウンロード(無料)'
                  )}
                </Button>
              </FormBottom>
            </FormList>
          </form>
        )}
      </Formik>
    </Root>
  );
};
